import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid, makeStyles } from '@material-ui/core';
import { useIntl } from 'react-intl';
import { OverviewChartLoading } from '~/modules/common/charts/dashboard';
import { KPI_TYPES } from '~/modules/common/charts/kpiTypes';
import { useProjectKPISummary } from './hooks';
import ProjectKPIItem from './ProjectKPIItem';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex'
  }
}));

const safeRatio = (n, d) => (d ? n / d : null);

export const ProjectKPIChart = ({ chartDataInput }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const {
    projectId,
    dateRange,
    scale,
    projectPermissions: { canViewTasks, canViewProjectBillingData },
    isExpenseProductEnabled
  } = chartDataInput;

  const {
    teamMembersTimesheetCountSummary,
    billableNonBillableHoursSummary,
    tasksCompletionCountSummary,
    tasksCount,
    loading
  } = useProjectKPISummary({
    projectId,
    dateRange
  });

  const canViewSPIIndex = canViewTasks;
  const canViewBIIndex = canViewProjectBillingData && isExpenseProductEnabled;

  const visibleKpiCount = [true, canViewBIIndex, canViewSPIIndex].filter(
    Boolean
  ).length;
  const gridSize = 12 / visibleKpiCount;

  const kpiPercentages = useMemo(
    () => ({
      tci: safeRatio(
        (teamMembersTimesheetCountSummary?.waitingTimesheetCount ?? 0) +
          (teamMembersTimesheetCountSummary?.approvedTimesheetCount ?? 0),
        teamMembersTimesheetCountSummary?.totalTimesheetCount
      ),
      bi: safeRatio(
        billableNonBillableHoursSummary?.billableHours,
        billableNonBillableHoursSummary?.totalHours
      ),
      spi: safeRatio(
        tasksCompletionCountSummary?.closedOnOrBeforeTaskEndDateCount,
        tasksCompletionCountSummary?.totalTaskCount
      )
    }),
    [
      teamMembersTimesheetCountSummary,
      billableNonBillableHoursSummary,
      tasksCompletionCountSummary
    ]
  );

  if (loading) return <OverviewChartLoading />;

  return (
    <Grid container className={classes.container} spacing={2}>
      <Grid item lg={gridSize} md={gridSize} xs={6}>
        <ProjectKPIItem
          title={formatMessage({
            id: 'projectKPI.timesheetComplianceIndex.label'
          })}
          value={kpiPercentages.tci}
          type={KPI_TYPES.TIMESHEETCOMPLIANCEINDEX}
          scale={scale}
          dateRange={dateRange}
          dataQeId="TimesheetComplianceIndexKPI"
        />
      </Grid>
      {canViewBIIndex && (
        <Grid item lg={gridSize} md={gridSize} xs={6}>
          <ProjectKPIItem
            title={formatMessage({
              id: 'projectKPI.billabilityIndex.label'
            })}
            value={kpiPercentages.bi}
            type={KPI_TYPES.BILLABILITYINDEX}
            scale={scale}
            dateRange={dateRange}
            dataQeId="BillabilityIndexKPI"
          />
        </Grid>
      )}
      {canViewSPIIndex && (
        <Grid item lg={gridSize} md={gridSize} xs={6}>
          <ProjectKPIItem
            title={formatMessage({
              id: 'projectKPI.schedulePerformanceIndex.label'
            })}
            value={kpiPercentages.spi}
            type={KPI_TYPES.SCHEDULEPERFORMANCEINDEX}
            scale={scale}
            dateRange={dateRange}
            dataQeId="SchedulePerformanceIndexKPI"
            tasksCount={tasksCount}
          />
        </Grid>
      )}
    </Grid>
  );
};

ProjectKPIChart.propTypes = {
  chartDataInput: PropTypes.shape({
    projectId: PropTypes.string.isRequired,
    dateRange: PropTypes.object,
    scale: PropTypes.string,
    projectPermissions: PropTypes.object,
    isExpenseProductEnabled: PropTypes.bool
  }).isRequired
};

export default ProjectKPIChart;
